// The vertical scale of a stage's swings: an offset in percentage points
// (−100…+100) to a unit position (−1…+1, 0 in the middle). Three kinds:
// linear; a symmetric log ("symlog") that spreads offsets near zero; and,
// the one the cards use (2026-10-08), a scale fitted to the games' own
// swings (`@chess/api-insights/fitted-scale`): on each side of zero the
// quantiles of that side's offsets (50th, 90th) are pinned to fixed heights,
// 0 to the middle and ±100 to the edges, linear between, so the dots spread
// over the plot whatever the stage's usual swing. A side with under
// `MIN_SIDE` games takes the symlog's shape. All keep 0 at 0, ±100 at the
// edges and the sign; the tooltip keeps the exact numbers.
import { fitKnots, knotTicks, positionOf, valueAt, type Knot } from '@chess/api-insights/fitted-scale';

export type ScaleKind = 'linear' | 'symlog' | 'fitted';

export interface VerticalScale {
  kind: ScaleKind;
  /** −100…+100 percentage points to −1…+1. */
  unit(offset: number): number;
  /** The inverse of `unit`. */
  offset(unit: number): number;
  /** Tick values, in percentage points, top to bottom. */
  ticks: number[];
}

/**
 * The symlog's constant, in percentage points: offsets well under it are
 * near-linear, well over it compressed. At 3, ±5 lands at ±0.28 of the half
 * height, ±20 at ±0.58 and ±50 at ±0.81, so the opening's usual 1–15 point
 * swings fill the middle of the plot while a lost game still reaches the edge.
 */
export const SYMLOG_C = 3;
const MAX = 100;
const clamp = (v: number) => Math.max(-MAX, Math.min(MAX, v));
const LOG_MAX = Math.log1p(MAX / SYMLOG_C);

const linear: VerticalScale = {
  kind: 'linear',
  unit: (v) => clamp(v) / MAX,
  offset: (u) => Math.max(-1, Math.min(1, u)) * MAX,
  ticks: [100, 50, 0, -50, -100],
};

const symlogUnit = (v: number) => {
  const c = clamp(v);
  return (Math.sign(c) * Math.log1p(Math.abs(c) / SYMLOG_C)) / LOG_MAX;
};

const symlog: VerticalScale = {
  kind: 'symlog',
  unit: symlogUnit,
  offset: (u) => {
    const c = Math.max(-1, Math.min(1, u));
    return Math.sign(c) * SYMLOG_C * Math.expm1(Math.abs(c) * LOG_MAX);
  },
  // Close to evenly spaced under the scale (0.28, 0.58, 1 of the half height).
  ticks: [100, 20, 5, 0, -5, -20, -100],
};

/** Games a side needs for its own quantiles; fewer take the symlog's shape. */
export const MIN_SIDE = 4;
/** The quantiles of a side's offsets pinned, and the heights (shares of the half) they are pinned to. */
const QUANTILES = [0.5, 0.9];
const HEIGHTS = [0.45, 0.8];
/** The symlog's shape as knots, for a side with too few games: ±5 and ±20 at their symlog heights. */
const SYMLOG_KNOTS: Knot[] = [[0, 0], [5, symlogUnit(5)], [20, symlogUnit(20)], [MAX, 1]];

/** One side's knots, from 0 to 100 in magnitude. */
function sideKnots(magnitudes: number[]): Knot[] {
  if (magnitudes.length < MIN_SIDE) return SYMLOG_KNOTS;
  return fitKnots(magnitudes, 0, MAX, QUANTILES, HEIGHTS);
}

/** The scale fitted to a set of offsets, each side to its own games. */
export function fittedScale(offsets: readonly number[]): VerticalScale {
  const up = sideKnots(offsets.filter((v) => v > 0).map((v) => Math.min(MAX, v)));
  const down = sideKnots(offsets.filter((v) => v < 0).map((v) => Math.min(MAX, -v)));
  const unit = (v: number) => (v >= 0 ? positionOf(up, v) : -positionOf(down, -v));
  const offset = (u: number) => (u >= 0 ? valueAt(up, u) : -valueAt(down, -u));
  const above = knotTicks(up).filter((t) => t > 0).reverse();
  const below = knotTicks(down).filter((t) => t > 0).map((t) => -t);
  return { kind: 'fitted', unit, offset, ticks: [...above, 0, ...below] };
}

/** The scale of a kind; `fitted` needs the offsets it is fitted to. */
export function verticalScale(kind: ScaleKind, offsets: readonly number[] = []): VerticalScale {
  return kind === 'fitted' ? fittedScale(offsets) : kind === 'symlog' ? symlog : linear;
}
